/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * TimePicker — A custom scroll-wheel-style time picker.
 * Matches the DatePicker's enterprise theme: zinc/emerald palette, portalled dropdown.
 * Zero external dependencies — built with React + Tailwind only.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from './utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

function parseTime(val: string): { h: string; m: string } {
  if (!val || !/^\d{2}:\d{2}$/.test(val)) return { h: '08', m: '00' };
  const [h, m] = val.split(':');
  return { h, m };
}

// ─── Component Props ──────────────────────────────────────────────────────────

export interface TimePickerProps {
  /** Current value in "HH:mm" format */
  value: string;
  /** Called with new "HH:mm" string on selection */
  onChange: (value: string) => void;
  /** Placeholder when no value selected */
  placeholder?: string;
  /** Whether the input is required */
  required?: boolean;
  /** Additional class names for the outer wrapper */
  className?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

// ─── TimePicker Component ─────────────────────────────────────────────────────

export function TimePicker({
  value,
  onChange,
  placeholder = 'Select time…',
  required,
  className,
  disabled,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; openUp: boolean }>({
    top: 0, left: 0, width: 0, openUp: false,
  });
  const [positioned, setPositioned] = useState(false);

  const { h, m } = parseTime(value);

  // ── Position the portal dropdown ──
  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const panelH = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < panelH + 8 && rect.top > panelH + 8;
    setPos({
      top: openUp ? rect.top - panelH - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
      openUp,
    });
  }, []);

  // Calculate position synchronously before paint to prevent flicker
  useLayoutEffect(() => {
    if (open) { reposition(); setPositioned(true); }
    else setPositioned(false);
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // ── Click outside to close ──
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const setHour = (newH: string) => onChange(`${newH}:${m}`);
  const setMinute = (newM: string) => onChange(`${h}:${newM}`);

  const stepHour = (dir: 1 | -1) => {
    const n = (parseInt(h) + dir + 24) % 24;
    setHour(String(n).padStart(2, '0'));
  };
  const stepMinute = (dir: 1 | -1) => {
    const n = (parseInt(m) + dir + 60) % 60;
    setMinute(String(n).padStart(2, '0'));
  };

  const displayValue = value ? `${h}:${m}` : '';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) { setOpen(p => !p); } }}
        className={cn(
          'flex items-center gap-2 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-left transition-colors',
          'focus:outline-none focus:border-zinc-900',
          disabled && 'opacity-50 cursor-not-allowed',
          open && 'border-zinc-900',
          className,
        )}
      >
        <Clock size={14} className="text-zinc-400 shrink-0" />
        <span className={cn('flex-1', value ? 'text-zinc-900 font-semibold tabular-nums' : 'text-zinc-400')}>
          {displayValue || placeholder}
        </span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: `${pos.top}px`,
            left: `${pos.left}px`,
            width: `${pos.width}px`,
            zIndex: 9999,
          }}
          className={cn(
            'bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden',
            positioned ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Set Exact Time</p>
            <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{h}:{m}</p>
          </div>

          {/* Spinners */}
          <div className="flex items-center justify-center gap-4 py-5 px-6">
            {/* Hour Spinner */}
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => stepHour(1)}
                className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"
              >
                <ChevronUp size={18} />
              </button>
              <select
                value={h}
                onChange={e => setHour(e.target.value)}
                className="w-16 text-center text-2xl font-bold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-xl py-2 focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none tabular-nums"
              >
                {HOURS.map(hr => (
                  <option key={hr} value={hr}>{hr}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => stepHour(-1)}
                className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"
              >
                <ChevronDown size={18} />
              </button>
              <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mt-0.5">Hour</span>
            </div>

            {/* Colon separator */}
            <span className="text-3xl font-bold text-zinc-300 -mt-5">:</span>

            {/* Minute Spinner */}
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => stepMinute(1)}
                className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"
              >
                <ChevronUp size={18} />
              </button>
              <select
                value={m}
                onChange={e => setMinute(e.target.value)}
                className="w-16 text-center text-2xl font-bold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-xl py-2 focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none tabular-nums"
              >
                {MINUTES.map(mn => (
                  <option key={mn} value={mn}>{mn}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => stepMinute(-1)}
                className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-all"
              >
                <ChevronDown size={18} />
              </button>
              <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mt-0.5">Min</span>
            </div>
          </div>

          {/* Quick-select row */}
          <div className="px-4 pb-3 flex flex-wrap gap-1.5 border-t border-zinc-100 pt-3">
            {['06:00', '08:00', '10:00', '12:00', '14:00', '18:00', '21:00'].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => { onChange(t); setOpen(false); }}
                className={cn(
                  'px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all',
                  value === t
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                    : 'bg-zinc-50 text-zinc-500 border-zinc-200 hover:border-zinc-400',
                )}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Done button */}
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full py-2 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700 transition-all"
            >
              Done
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
